/**
 * E2E renderer test probe — exposes a device's reported video codec to
 * Playwright via `globalThis.__irisSourceCodecProbe`.
 *
 * The NDI/recording pickers only surface a codec when it is REFUSED (H.265
 * wording on the card); an allowed or still-unknown codec renders identically.
 * ndi_output_mixed_codec.spec.ts needs a genuine H.264 source next to an
 * H.265 one and the lab's "H.264" cameras are not stable facts (a PTZOptics
 * unit tagged fingerprinted streamed H.265 on PR #1811 run 34951772734), so
 * the spec reads the encoder-reported codec and keeps adding candidates until
 * one is H.264. The value is the state-sync string the pickers gate on
 * (`status.video.status.codec`, e.g. "video/H264"), '' while unknown.
 *
 * Per `think-in-systems` (ai.knowledge/global): one read primitive, pure core
 * exported for unit tests. Same IRIS_E2E_TEST=1 gate and single-statement
 * install as device-probe.ts.
 */
import type { Device } from '@studio/models/device';
import { locateProxy } from '#~/agent/index.js';

interface SourceCodecProbe {
  /** Encoder-reported video codec for the device, '' while the encoder has not parsed the stream. */
  videoCodec(deviceId: Device['id']): string;
}

type ProxyLike = {
  state?: {
    status?: {
      device?: Record<string, { video?: { status?: { codec?: string } } } | undefined>;
    };
  };
};

/** Pure read: the codec string state-sync carries for `deviceId`, or '' when absent. */
export function readVideoCodec(proxy: ProxyLike | undefined, deviceId: Device['id']): string {
  return proxy?.state?.status?.device?.[deviceId]?.video?.status?.codec ?? '';
}

const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

if (isE2eEnabled()) {
  const probe: SourceCodecProbe = {
    videoCodec(deviceId: Device['id']): string {
      return readVideoCodec(locateProxy(deviceId) as ProxyLike | undefined, deviceId);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisSourceCodecProbe = probe;
}
